<template>
  <page title="Reports">
    <template #actions>
      <CombinedSearch
        label=""
        placeholder="Search Reports"
        input-id="reports-search"
        :show-clear-button="true"
        class="reports-search"
        @update:searchQuery="search = $event"
      />
    </template>
    <div class="reports-grid" :style="{ '--cols': columnLayout.length }">
      <div
        v-for="(columnGroups, colIdx) in columnLayout"
        :key="colIdx"
        class="reports-column"
      >
        <template v-for="name in columnGroups">
          <section
            v-if="filteredGroups[name]"
            :key="name"
            class="reports-group"
          >
            <h2 class="reports-group__label">{{ name }}</h2>
            <accordion
              :items="filteredGroups[name].reports"
              class="reports-accordion"
            >
              <template #header="{ item }">
                <a :href="item.url" data-no-toggle class="reports-link">
                  {{ item.name }}
                </a>
              </template>
              <template #content="{ item }">
                <ul v-if="item.details.length" class="reports-details">
                  <li v-for="detail in item.details" :key="detail">
                    {{ detail }}
                  </li>
                </ul>
              </template>
            </accordion>
            <p
              v-if="!filteredGroups[name].reports.length"
              class="reports-empty"
            >
              No Reports Available
            </p>
          </section>
        </template>
      </div>
    </div>
  </page>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useStore } from "vuex";

import Page from "@/components/Page.vue";
import CombinedSearch from "@/components/Seranova/SearchInput/CombinedSearch.vue";

interface Report {
  name: string;
  url: string;
  details: string[];
}

interface ReportGroup {
  icon?: string;
  reports: Report[];
}

type ReportGroups = Record<string, ReportGroup>;

const LAYOUTS = {
  lg: [
    ["Dispatch", "Financials"],
    ["Accounting", "Company"],
    ["Memberships", "Customers"]
  ],
  md: [
    ["Dispatch", "Financials", "Accounting"],
    ["Memberships", "Company", "Customers"]
  ],
  sm: [
    [
      "Dispatch",
      "Accounting",
      "Memberships",
      "Financials",
      "Company",
      "Customers"
    ]
  ]
} as const;

const store = useStore();
const reportGroups = ref<ReportGroups>({});
const search = ref("");
const breakpoint = ref<keyof typeof LAYOUTS>("lg");

const filteredGroups = computed<ReportGroups>(() => {
  if (!search.value) return reportGroups.value;
  const term = search.value.toLowerCase();
  return Object.keys(reportGroups.value).reduce<ReportGroups>((acc, key) => {
    const group = reportGroups.value[key];
    const matched = group.reports.filter(
      (r) =>
        r.name.toLowerCase().includes(term) ||
        r.details.some((d) => d.toLowerCase().includes(term))
    );
    acc[key] = { ...group, reports: matched };
    return acc;
  }, {});
});

const columnLayout = computed(() => LAYOUTS[breakpoint.value]);

let lgQuery: MediaQueryList | null = null;
let mdQuery: MediaQueryList | null = null;

const updateBreakpoint = () => {
  if (lgQuery?.matches) breakpoint.value = "lg";
  else if (mdQuery?.matches) breakpoint.value = "md";
  else breakpoint.value = "sm";
};

onMounted(async () => {
  lgQuery = window.matchMedia("(min-width: 1200px)");
  mdQuery = window.matchMedia("(min-width: 768px)");
  updateBreakpoint();
  lgQuery.addEventListener("change", updateBreakpoint);
  mdQuery.addEventListener("change", updateBreakpoint);

  const res = await store.dispatch("fetchReportGroups");
  const data = res.data as ReportGroups;
  Object.keys(data).forEach((key) => {
    data[key].reports.sort((a, b) => a.name.localeCompare(b.name));
  });
  reportGroups.value = data;
});

onUnmounted(() => {
  lgQuery?.removeEventListener("change", updateBreakpoint);
  mdQuery?.removeEventListener("change", updateBreakpoint);
});
</script>

<style lang="scss" scoped>
.reports-search {
  min-width: 360px;

  :deep(.search-input) {
    background-color: #ffffff;
    padding-top: 0.625rem;
    padding-bottom: 0.625rem;
  }
}

.reports-grid {
  display: grid;
  grid-template-columns: repeat(var(--cols, 3), minmax(0, 1fr));
  gap: 0 1.5rem;
  align-items: start;
  padding-bottom: 2rem;
}

.reports-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.reports-group {
  background: #f9fafb;
  border-radius: 0.5rem;
  padding: 0.875rem 1rem 0.5rem;
}

.reports-group__label {
  margin: 0 0 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #6b7280;
}

.reports-accordion {
  background-color: transparent !important;
  padding: 0 !important;

  :deep(.accordion-item) {
    border-bottom-color: #e5e7eb;
  }

  :deep(.acc-item-header) {
    padding: 0.3125rem 0;
    font-size: 1rem;
    line-height: 1.5rem;

    .toggle-icon {
      color: #374151;
      font-size: 0.6875rem;
    }
  }

  :deep(.acc-item-inner-content) {
    padding-bottom: 0.625rem;
  }
}

.reports-link {
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 400;
  color: #2563eb;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.reports-details {
  margin: 0.25rem 0 0;
  padding-left: 0.625rem;
  border-left: 2px solid #d1d5db;
  font-size: 0.75rem;
  line-height: 1.5;
  color: #4b5563;
  list-style: none;

  li {
    margin: 0.125rem 0;
  }
}

.reports-empty {
  padding: 0.75rem 0;
  text-align: center;
  font-size: 0.875rem;
  color: #9ca3af;
}
</style>
